import useSWR from "swr";
import { useMemo } from "react";

import { SWR_KEY_PREFIX } from "../../constants/constants";
import { UserGroup } from "../../domain/UserGroup";
import { useServicesContext } from "../../providers/ServicesProvider";

interface ResolvedUserGroups {
    userGroupsById: Record<string, UserGroup>;
    unresolvedIds: string[];
}

/**
 * Resolves user group ids to their groups.
 *
 * `GET /api/usergroups` has no id filter, so this is one read per id. Callers hold small, bounded sets
 * — the bulk upload record's target groups come from what an admin picked in the Product Access step.
 *
 * Settled rather than all: an id that fails to resolve is a normal outcome here, not an error. A
 * rejected group is usually a *deleted* one, and the reason to show it at all is that it no longer
 * exists — rejecting the batch would blank a view whose other groups resolved fine. Those ids come
 * back in `unresolvedIds` for the caller to render as raw ids; `error` is reserved for the whole
 * fetch failing.
 */
export const useUserGroupsByIds = ({ ids = [] }: { ids?: string[] }) => {
    const { userService } = useServicesContext();

    // Sorted and deduped so callers passing the same ids in a different order share a cache entry.
    const uniqueIds = useMemo(() => Array.from(new Set(ids.filter(Boolean))).sort(), [ids]);

    // Null when there is nothing to resolve, so a record with no target groups never fetches.
    const key = uniqueIds.length ? `${SWR_KEY_PREFIX.GROUPS}/api/usergroups?ids=${uniqueIds.join(",")}` : null;

    const { data, error } = useSWR<ResolvedUserGroups, Error>(key, async () => {
        const results = await Promise.allSettled(uniqueIds.map((id) => userService.getUserGroupById({ id })));

        return results.reduce<ResolvedUserGroups>(
            (resolved, result, index) => {
                const id = uniqueIds[index];

                if (result.status === "fulfilled" && result.value) {
                    resolved.userGroupsById[id] = result.value;
                } else {
                    resolved.unresolvedIds.push(id);
                }

                return resolved;
            },
            { userGroupsById: {}, unresolvedIds: [] }
        );
    });

    return {
        error,
        // Gated on `key` so a hook with nothing to fetch reports idle rather than a load that will
        // never finish. Matches `useBulkUploadDetails`.
        isLoading: Boolean(key) && !error && !data,
        unresolvedIds: data?.unresolvedIds ?? [],
        userGroupsById: data?.userGroupsById ?? {}
    };
};
